'use client'
import React, { useEffect, useState } from 'react'

import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Box, LinearProgress, IconButton,
  Card, Alert
} from '@mui/material'
// Import MUI icons at the top of your file
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GridOnIcon from '@mui/icons-material/GridOn';         // For Excel (xlsx)
import DescriptionIcon from '@mui/icons-material/Description'; // For csv and default
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import DialogCloseButton from './DialogCloseButton';
import MaterialBox from '../../components/MaterialBox';
import MaterialButton from '../../components/MaterialButton';


export default function UploadFile({
  open,
  setOpen,
  onUploadSuccess,
  fileUploading = false,
  uploadSuccess = false,
  setUploadSuccess, setFileUploading,
  fileTypes = ['csv', 'xlsx', 'pdf'],
  clearFileOnSuccess
}) {
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    if(clearFileOnSuccess){
      handleClose();
    }
  },[clearFileOnSuccess])

  const handleClose = () => {
    setSelectedFile(null)    // reset file
    setUploadSuccess(false)  // reset success state
    setFileUploading(false)  // reset uploading state
    setOpen(false)                // close dialog
  }


  const handleFileSelect = (e) => {
    const file = e.target.files?.[0]

    if (!file) return

    const ext = file.name.split('.').pop()?.toLowerCase()

    if (ext && fileTypes.includes(ext)) {
      setSelectedFile(file)
    } else {
      alert(`Only ${fileTypes.join(', ')} files are allowed!`)
    }
  }

  const handleUpload = () => {
    if (!selectedFile) return
    onUploadSuccess(selectedFile) // parent triggers API
  }

  const formatFileSize = (size) => {
    return size < 1024
      ? size + ' B'
      : size < 1024 * 1024
        ? (size / 1024).toFixed(1) + ' KB'
        : (size / (1024 * 1024)).toFixed(1) + ' MB'
  }

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
  
    switch (ext) {
      case 'pdf':
        return <PictureAsPdfIcon sx={{ color: '#E53935', fontSize: 24 }} />;
      case 'xlsx':
        return <GridOnIcon sx={{ color: '#43A047', fontSize: 24 }} />;
      case 'csv':
        return <DescriptionIcon sx={{ color: '#1E88E5', fontSize: 24 }} />;
      default:
        return <DescriptionIcon sx={{ fontSize: 24 }} />;
    }
  };

  return (
    <Dialog open={open} fullWidth maxWidth='sm' sx={{ '& .MuiDialog-paper': { borderRadius: 2, overflow: 'visible' } }}>
      <DialogCloseButton onClick={handleClose} disableRipple>
        <CloseIcon sx={{ fontSize: 24, color: 'primary.dark' }} />
      </DialogCloseButton>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <CloudUploadIcon sx={{ width: 34, height: 34 }} />
        <Box>
          <Typography variant='h6'>
            Upload files
          </Typography>
          <Typography variant='subtitle2'>Select and upload the files of your choice</Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 5 }}>
        {!selectedFile ? (
          <Box
            sx={{
              textAlign: 'center',
              border: '2px dashed #ccc',
              p: { xs: 3, sm: 5 },
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'background-color 0.3s ease-in-out',
              '&:hover': {
                backgroundColor: '#f0f0f0'
              }
            }}
            onClick={() => document.getElementById('fileInput')?.click()}
          >
            <CloudUploadIcon sx={{ width: 24, height: 24 }} />
            <Typography variant='h5'>
              Choose a file or drag & drop it here
            </Typography>
            <Typography variant='subtitle2'>File Format: {fileTypes.join(', ')}</Typography>
            <input
              type='file'
              id='fileInput'
              accept={fileTypes.map(f => '.' + f).join(',')}
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
          </Box>
        ) : (
          <Box>
            <Card>
            <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: '4px',
                  mr: 2,
                  backgroundColor: '#f0f0f0'
                }}
              >
                {getFileIcon(selectedFile.name)}
              </Box>
              <Box sx={{ flexGrow: 1, p: 0 }}>
                <Typography variant='body1' sx={{ fontWeight: 'bold' }}>
                  {selectedFile.name}
                </Typography>
                <Typography variant='caption'>{formatFileSize(selectedFile.size)}</Typography>
              </Box>
              <IconButton size='small' onClick={() => { setUploadSuccess(false); setFileUploading(false); setSelectedFile(null); }}>
                <CloseIcon sx={{ fontSize: 24 }} />
              </IconButton>
            </Box>
            </Card>

            {fileUploading && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress variant='indeterminate' />
                <Typography variant='caption'>Uploading...</Typography>
              </Box>
            )}

            {uploadSuccess && !fileUploading && (
              <Box sx={{ mt: 3 }}>
                <Alert severity='success'>File uploaded successfully!</Alert>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, mt: 3 }}>
        {uploadSuccess ? (
          <MaterialBox sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
            <MaterialButton variant='outlined' color='dark' onClick={handleClose} sx={{ fontWeight: 'bold' }}>
              Close
            </MaterialButton>
          </MaterialBox>
        ) : (
          <MaterialBox sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
            <MaterialButton
              variant='outlined'
              color='dark'
              disabled={fileUploading}
              onClick={handleClose}
              sx={{ fontWeight: 'bold' }}
            >
              Cancel
            </MaterialButton>
            <MaterialButton
              variant='gradient'
              color='dark'
              onClick={handleUpload}
              disabled={!selectedFile || fileUploading}
              sx={{ fontWeight: 'bold' }}
            >
              {fileUploading ? 'Uploading...' : 'Upload'}
            </MaterialButton>
          </MaterialBox>
        )}
      </DialogActions>
    </Dialog>
  )
}

